/**
 * Utilities related to our Rich Text Editor.
 *
 * Much of this is ripped straight from the html paste example
 */
import { deserializeHTMLToSlate, serializeSlateToHTML } from "@resource/atlas";
import { Placeholder } from "@resource/common";
import DOMPurify from "isomorphic-dompurify";
import _ from "lodash";
import { ReactNode } from "react";
import { Descendant, Text } from "slate";

export const EMPTY_SLATE = [{ children: [{ text: "" }], type: "paragraph" }];

const HTMLToNewEditor = (
  htmlString: string,
  wrapIfNecessary = true
): Descendant[] => {
  const cleaned = htmlString
    // Remove these zero width spaces
    .replace(/\uFEFF/g, "")
    // Drop this stupid cursor that quill just appends for no reason sometimes
    .replace(/<span class="ql-cursor"><\/span>/g, "");
  return deserializeHTMLToSlate(cleaned, wrapIfNecessary);
};

const SlateToHTML = (
  nodeOrNodes: Descendant[] | Descendant,
  availablePlaceholders: Placeholder[]
): string =>
  serializeSlateToHTML(
    nodeOrNodes,
    _.map(availablePlaceholders, ({ id, label }) => ({
      id,
      name: label,
    }))
  );

type Node = Descendant | Descendant[];

const withRenderedVariables = <T extends Node>(
  node: T,
  replacementData: Record<string, string>,
  throwOnMissing = false,
  parent?: Descendant
): T => {
  if (Array.isArray(node)) {
    return node.map((child: Descendant) =>
      withRenderedVariables(child, replacementData, throwOnMissing)
    ) as T;
  }

  if (Text.isText(node)) {
    return node;
  }

  const children = node.children.flatMap((n: Descendant) =>
    withRenderedVariables(n, replacementData, throwOnMissing, node)
  );

  switch (node.type) {
    case "variable": {
      const rawReplacement = replacementData[node.id];
      if (!rawReplacement && throwOnMissing) {
        throw new Error("Missing Variable");
      }
      return HTMLToNewEditor(rawReplacement, !parent) as T;
    }
    default:
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return {
        ...node,
        children,
      };
  }
};

const DEFAULT_ALLOWED_TAGS = ["span", "a", "br"];
const DEFAULT_ADD_ATTR = ["target"];

const getSanitizedReplacementData = ({
  data,
  config = {
    ALLOWED_TAGS: DEFAULT_ALLOWED_TAGS,
    ADD_ATTR: DEFAULT_ADD_ATTR,
  },
}: {
  data: Record<string, unknown>;
  config?: DOMPurify.Config;
}): Record<string, ReactNode> =>
  _.mapValues(data, (val) => {
    if (typeof val === "string" && val !== "") {
      const sanitizedValue = DOMPurify.sanitize(val, {
        ...config,
        RETURN_DOM: false,
        RETURN_DOM_FRAGMENT: false,
      });
      return <span dangerouslySetInnerHTML={{ __html: sanitizedValue }} />;
    }
    return undefined;
  });

const isEmpty = (data: Descendant[]): boolean => _.isEqual(data, EMPTY_SLATE);

const nonEmptyValidator = (name: string) => (data: Descendant[]) =>
  _.negate(isEmpty)(data) || `${name} can't be empty`;

const allVariablesReplacedValidator =
  (replacementData: Record<string, string>) => (data: Descendant[]) => {
    try {
      withRenderedVariables(data, replacementData, true);
    } catch {
      return "Some tokens are missing values.";
    }
    return true;
  };

export {
  allVariablesReplacedValidator,
  getSanitizedReplacementData,
  HTMLToNewEditor,
  isEmpty,
  nonEmptyValidator,
  SlateToHTML,
  withRenderedVariables,
};
